import { useState } from 'react';
import api from '../utils/api';

/**
 * Clear instructions for Rocket League challenges + optional Verify button.
 * Auto-verification requires replays on ballchasing.com.
 */
export default function RocketLeagueInstructions({ epicUsername = '', onVerified, compact = false, dark = false }) {
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const handleVerify = async () => {
    const username = (typeof epicUsername === 'string' ? epicUsername : '').trim();
    if (!username) {
      setVerifyResult({ verified: false, message: 'Enter your Epic username first.' });
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.get('/verify-epic-username', { params: { username } });
      setVerifyResult(data);
      if (data?.verified && onVerified) onVerified();
    } catch (e) {
      setVerifyResult({ verified: false, message: e?.response?.data?.error ?? e.message ?? 'Verification failed.' });
    }
    setVerifying(false);
  };

  if (compact) {
    const bg = dark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)';
    const border = dark ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.3)';
    const titleColor = dark ? '#93c5fd' : '#1e40af';
    const textColor = dark ? '#cbd5e1' : '#334155';
    const linkColor = dark ? '#60a5fa' : '#2563eb';
    return (
      <div style={{ marginTop: 8, padding: 10, background: bg, borderRadius: 6, border: `1px solid ${border}`, fontSize: '0.85em' }}>
        <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: titleColor }}>Third-party: Ballchasing (required for auto-payout)</p>
        <p style={{ margin: '0 0 8px 0', color: textColor }}>
          Auto-payout uses <strong>Ballchasing</strong> (not Epic Games). Create a free account, then both players upload the replay to{' '}
          <a href="https://ballchasing.com/upload" target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline' }}>ballchasing.com/upload</a>. Use your Epic name exactly as in-game.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              backgroundColor: verifying ? '#64748b' : '#0284c7',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: verifying ? 'not-allowed' : 'pointer',
            }}
          >
            {verifying ? 'Checking…' : 'Verify'}
          </button>
          {verifyResult && (
            <span style={{ color: verifyResult.verified ? '#34d399' : '#f87171', fontSize: 12 }}>{verifyResult.message}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, padding: 14, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', fontSize: '0.9em' }}>
      <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: '#0369a1' }}>
        Rocket League auto-payout uses Ballchasing (third-party)
      </p>
      <p style={{ margin: '0 0 12px 0', color: '#0c4a6e', lineHeight: 1.6 }}>
        <strong>Ballchasing</strong> (ballchasing.com) is a separate replay hosting service — <strong>not</strong> owned by Epic Games.
        You need a <strong>free Ballchasing account</strong> to upload replays (typically from <strong>PC</strong>).
      </p>
      <ol style={{ margin: '0 0 12px 0', paddingLeft: 20, color: '#0c4a6e', lineHeight: 1.6 }}>
        <li>Enter your <strong>Epic Games display name</strong> exactly as it appears in Rocket League.</li>
        <li>After the match, <strong>upload the replay file</strong> to <a href="https://ballchasing.com/upload" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', fontWeight: 600 }}>ballchasing.com/upload</a>.</li>
        <li><strong>Both</strong> players should upload. Replays are stored on your PC after each game.</li>
        <li>Set visibility to <strong>public</strong> or <strong>unlisted</strong> so we can read the score.</li>
      </ol>
      <p style={{ margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85em' }}>
        No Ballchasing upload means we cannot pay out automatically — you can still confirm the winner together in the app.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          style={{
            padding: '8px 16px',
            fontSize: 13,
            backgroundColor: verifying ? '#94a3b8' : '#0284c7',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: verifying ? 'not-allowed' : 'pointer',
            fontWeight: 500,
          }}
        >
          {verifying ? 'Checking…' : 'Verify my username'}
        </button>
        {verifyResult && (
          <span style={{ color: verifyResult.verified ? '#059669' : '#dc2626', fontSize: 13 }}>
            {verifyResult.message}
          </span>
        )}
      </div>
    </div>
  );
}
